import { Injectable } from '@angular/core';
import { InAppBrowser, InAppBrowserObject, InAppBrowserOptions } from '@awesome-cordova-plugins/in-app-browser/ngx';
import { Capacitor } from '@capacitor/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, concatMap, first, map, skipWhile, switchMap, tap } from 'rxjs/operators';

import { DBrowserPremiadosAndroid, DBrowserPremiadosiOS } from '../../private/shared/models/browser-configuration';
import { DContextosPremiados, IContextosPremiados } from '../../private/shared/models/url-contextos';
import { ProgramClosureService } from '../../private/shared/services/program-closure.service';
import { GTMEventsCrash } from '../enums/Crashlytics';
import { IPremiadosResp } from '../models/ProgramClose';
import { getSeccionesPremiados, getTokenPremiados } from './../../store/actions/premiados.actions';
import { AppState } from './../../store/app-reducers';
import { CrashlyticsService } from './crashlytics/crashlytics.service';
import { SharedServiceService } from './shared-service.service';
import { ToastControllerService } from './toast-controller.service';

@Injectable({
  providedIn: 'root'
})
export class Premia2Service {

  public isLoadingService = new BehaviorSubject(false);
  public customBrowser: InAppBrowserObject = {} as InAppBrowserObject;

  private contextConfiguration: IContextosPremiados = DContextosPremiados;
  private iosBrowserConfiguration: InAppBrowserOptions = DBrowserPremiadosiOS;
  private androidBrowserConfiguration: InAppBrowserOptions = DBrowserPremiadosAndroid;
  private hasSharingVisible = true;
  private browserClosed = false;

  constructor(
    private toastControllerService: ToastControllerService
    , private programClousure: ProgramClosureService
    , private sharedService: SharedServiceService
    , private crashlytics: CrashlyticsService
    , private inAppBrowserService: InAppBrowser
    , private store: Store<AppState>
  ) { }

  public openPremiados = (programa: number, nud: number, bodega: number): Observable<IPremiadosResp> => {
    this.browserClosed = false;
    this.isLoadingService.next(true);
    let premia2Config: any;
    let logOutIdentifier = '';
    let identifierOptionalLogout = '';
    let loginIdentifierOptional = '';
    return this.programClousure.getRemoteConfigFireStone(programa)
      .pipe(
        tap(res => {
          const url = res.premia2.api ? `${res.premia2.api.url_base}/` : '';
          const urlLogin = `${url}${res.premia2.api.context}`;
          const urlSections = `${url}${res.premia2.sections.context}`;
          const headers = res.premia2.api ? res.premia2.api.headers : '';
          const body = { nud: String(nud), warehouse: String(bodega) };
          this.store.dispatch(getTokenPremiados({ url: urlLogin, body, headers }));
          this.store.dispatch(getSeccionesPremiados({ url: urlSections, body, headers }));
        }),
        switchMap(premia2Config => this.store.select('TokenPremiados')
          .pipe(
            skipWhile(x => !x.success && !x.failed),
            map(token => ({ premia2Config: premia2Config.premia2, token }))
          )
        ),
        concatMap(config => this.store.select('SectionsPremiados')
          .pipe(
            skipWhile(x => !x.success && !x.failed),
            map(resp => ({ ...config, sections: resp.data }))
          )
        ),
        first(),
        map(resp => {
          premia2Config = resp.premia2Config;
          logOutIdentifier = premia2Config.logout ? premia2Config.logout.identifier : '';
          identifierOptionalLogout = premia2Config.logout ? premia2Config.logout.optionalIdentifier : '';
          loginIdentifierOptional = premia2Config.login ? premia2Config.login.optionalIdentifier : '';
          this.contextConfiguration = premia2Config && premia2Config.context_configuration ? premia2Config.context_configuration : DContextosPremiados;
          this.iosBrowserConfiguration = premia2Config && premia2Config.browser_configuration ? premia2Config.browser_configuration.ios_configuration : DBrowserPremiadosiOS;
          this.androidBrowserConfiguration = premia2Config && premia2Config.browser_configuration ? premia2Config.browser_configuration.android_configuration : DBrowserPremiadosAndroid;
          let urlLogin = '';
          let platform = '';
          if (resp.token.success) {
            urlLogin = `${premia2Config.login.url_base}/${premia2Config.login.context}?${premia2Config.login.param}=${resp.token.data.token}`;
            platform = Capacitor.getPlatform();
            this.isLoadingService.next(false);
            if (!resp.token.data.success) {
              this.toastControllerService.presentToast(resp.token.data.message || '', 'grayDemon',
                'warning', 6000, 'ion-simple-toast');
              this.isLoadingService.next(false);
            }
          }
          return ({
            success: resp.token.success,
            failed: !resp.token.success,
            urlLogin,
            platform,
            logOutIdentifier,
            identifierOptionalLogout,
            loginIdentifierOptional,
            redirectTo: premia2Config.sections.param,
            sections: resp.sections,
          });
        }),
        catchError((err: Error) => {
          this.toastControllerService.presentToast(err.message, 'grayDemon',
            'warning', 6000, 'ion-simple-toast');
          return of({
            success: false,
            failed: true,
            urlLogin: '',
            platform: '',
            logOutIdentifier: '',
            identifierOptionalLogout: '',
            loginIdentifierOptional: '',
            redirectTo: '',
            sections: [],
          });
        }),
      );
  }

  public launchBrowser(url: string, platform: string, logOutIdentifier: string, identifierOptionalLogout: string, loginIdentifierOptional: string) {
    const options: InAppBrowserOptions = platform === 'android' ? this.androidBrowserConfiguration : this.iosBrowserConfiguration
    this.customBrowser = this.inAppBrowserService.create(url, '_blank', options)
    this.customBrowser.show()

    if (Capacitor.isNativePlatform()) {
      const observableBefore = this.customBrowser.on('beforeload')
      const observableStart = this.customBrowser.on('loadstart')

      observableBefore.subscribe(event => {
        this.manageEventsBefore(event.url)
      })

      observableStart.subscribe(event => {
        this.manageEventsStart(event.url, logOutIdentifier, identifierOptionalLogout, loginIdentifierOptional)
      })
    }
  }

  private manageEventsBefore(url: string) {
    let hasShare = false
    const keysShareContext = Object.keys(this.contextConfiguration.share_content)
    hasShare = keysShareContext
      .filter(keyContextShare => url.includes(this.contextConfiguration.share_content[keyContextShare])).length > 0
      ? true : false

    if (hasShare) {
      if (this.hasSharingVisible) {
        this.sharedService.urlSharing(
          url,
          this.callbackNextShareUrl.bind(this),
          this.callbackErrorShareUrl.bind(this)
        )
        this.hasSharingVisible = false
      }
    } else if (!this.browserClosed) {
      this.customBrowser._loadAfterBeforeload(url)
    }
  }

  private manageEventsStart(url: string, logOutIdentifier: string, identifierOptionalLogout: string, loginIdentifierOptional: string) {
    if (url.includes(logOutIdentifier)) {
      this.browserClosed = true
      this.customBrowser.close()
    } else if (!url.includes(loginIdentifierOptional) && url.includes(identifierOptionalLogout)) {
      this.browserClosed = true
      this.customBrowser.close()
    }
  }

  private callbackNextShareUrl() {
    this.hasSharingVisible = true
  }

  private async callbackErrorShareUrl(error: any) {
    this.hasSharingVisible = true
    await this.crashlytics.sendCrashPlatform(GTMEventsCrash.sharedUrlPremia2Plugin, error);
  }

  public getConfigButtons(programa: number) {
    return this.programClousure.getRemoteConfigFireStone(programa)
  }

}
